import { CCStyling } from "../../cclib/CCStyling";
import { CCStructureItem } from "../../cclib/types/structure";
import {
  contributionGrid,
  deptEMForm,
  instructionsStyling,
  preamble,
  setIsMentorField
} from "../shared/structure";

// Bottom of seniorStructure's definition has a .map()
// which sets isMentorField to this value.
const ALL_FIELDS_MENTOR = true;
// Also check the bottom of seniorStructure's definition for other .map() calls
const paragraphStyling = CCStyling.fromObject({
  "max-width": "1024px",
  "min-height": "7.5rem"
});

export const DISCUSSED_RADIO_OPTIONS = [
  "Discussed, no notes to record",
  "Discussed, with notes as follows"
];
const discussedRadio = (key: string, text?: string) => {
  const main: CCStructureItem = {
    type: "radio",
    key: key,
    options: DISCUSSED_RADIO_OPTIONS
  };
  if (text) {
    main.text = text;
  }
  return main;
};

export const seniorStructure: CCStructureItem[] = [
  ...[
    ...preamble("ANNUAL MEETINGS FOR SENIOR FACULTY"),
    {
      type: "header",
      name: "Part 1: Annual Meeting Documentation"
    },
    {
      type: "header",
      name: "Overview"
    },
    {
      type: "instructions",
      additionalCss: instructionsStyling,
      text: "Note: The School of Medicine is committed to providing a supportive atmosphere to assist all faculty in their academic careers. With respect to senior faculty, this is especially important as longevity permits individuals to stay in the workplace much longer than in previous generations. Our collective goal is to work toward guided and planned career evolution for senior faculty that optimally balances individual and institutional needs and ensures that any kind of transition -- such as short- or long-term loss of funding, taking on different roles in education or administration, reducing clinical loads, or moving to emeritus status -- occurs smoothly, ideally with anticipatory planning."
    },
    {
      type: "instructions",
      additionalCss: instructionsStyling,
      text: "Annual meetings between senior faculty members and department chairs (or their designates) have the potential to provide a forum for this to occur in a systematic way, including the exchange of information regarding current and future directions of the faculty member's research, teaching or clinical activities and their relationship to a department's programmatic needs and academic planning efforts."
    },
    {
      type: "instructions",
      additionalCss: instructionsStyling,
      text: "Effective February 1, 2011, the School requires each senior faculty member (Professor) to have an annual meeting with his or her chair (or designate). In addition to department-specific issues, the topics listed in this document should be part of the discussion and an accompanying set of available resources should be shared with the faculty member. This checklist with comments from the Chair/Chief should be kept on file in the department."
    },
    {
      type: "header",
      name: "1.\tProportionality of Contribution"
    },
    discussedRadio(
      "anticipated_proportion_radio",
      "Anticipated change in proportionality of contribution:"
    ),
    {
      type: "paragraph",
      key: "anticipated_proportion"
    },
    discussedRadio(
      "anticipated_admin_radio",
      "Anticipated entry into or exit from administrative responsibilities:"
    ),
    {
      type: "paragraph",
      key: "anticipated_admin"
    },
    {
      type: "header",
      name: "2.\tScholarly Activities"
    },
    discussedRadio(
      "recent_pub_radio",
      "Recent publications and/or works in progress:"
    ),
    {
      type: "paragraph",
      key: "recent_pub"
    },
    discussedRadio(
      "research_funding_radio",
      "Current and anticipated research funding:"
    ),
    {
      type: "paragraph",
      key: "research_funding"
    },
    {
      type: "header",
      name: "3.\tTeaching Activities"
    },
    discussedRadio(
      "teaching_activities_radio",
      "Current and anticipated teaching activities (broadly defined to include the classroom, laboratory, or clinical setting; advising; mentoring, program building; or curricular innovation.)"
    ),
    {
      type: "paragraph",
      key: "teaching_activities"
    },
    {
      type: "header",
      name: "4.\tClinical Activities (if applicable)"
    },
    discussedRadio(
      "clinical_activities_radio",
      "Current and anticipated clinical activities"
    ),
    {
      type: "paragraph",
      key: "clinical_activities"
    },
    {
      type: "header",
      name: "5.\tMentorship Activities"
    },
    discussedRadio("mentorship_activities_radio"),
    {
      type: "paragraph",
      key: "mentorship_activities"
    },
    {
      type: "header",
      name: "6.\tLeadership/Administrative Activities"
    },
    discussedRadio("leadership_activities_radio"),
    {
      type: "paragraph",
      key: "leadership_activities"
    },
    {
      type: "header",
      name: "7.\tQuality Improvement Projects"
    },
    discussedRadio("quality_improvement_projects_radio"),
    {
      type: "paragraph",
      key: "quality_improvement_projects"
    },
    {
      type: "header",
      name: "8.\tAdvocacy Activities"
    },
    discussedRadio("advocacy_activities_radio"),
    {
      type: "paragraph",
      key: "advocacy_activities"
    },
    {
      type: "header",
      name: "9.\tFinancial Issues"
    },
    discussedRadio(
      "salary_support_radio",
      "Multiple-year plan for salary support:"
    ),
    {
      type: "paragraph",
      key: "salary_support"
    },
    discussedRadio(
      "academic_support_radio",
      "Multiple-year plan for academic support (staff, travel, supplies, etc.)"
    ),
    {
      type: "paragraph",
      key: "academic_support"
    },
    discussedRadio(
      "laboratory_space_radio",
      "Implications of the above, if any, on laboratory space:"
    ),
    {
      type: "paragraph",
      key: "laboratory_space"
    },

    {
      type: "header",
      name: "10.\tPlanned sabbatical leaves or other leaves of absence"
    },
    discussedRadio("planned_sabbatical_radio"),
    {
      type: "paragraph",
      key: "planned_sabbatical"
    },

    {
      type: "header",
      name: "11.\tCurrent or anticipated service activities"
    },
    discussedRadio(
      "service_activities_radio",
      " (e.g., junior faculty mentoring; department, School or University committees or task forces, etc.)"
    ),
    {
      type: "paragraph",
      key: "service_activities"
    },

    {
      type: "header",
      name: "12.\tPersonal issues (if applicable)"
    },
    discussedRadio("personal_issues_radio"),
    {
      type: "paragraph",
      key: "personal_issues"
    },

    {
      type: "header",
      name: "13.\tGoing Forward"
    },
    discussedRadio(
      "plans_radio",
      "Short (1-3 years) and long-term (5-10 years) plans."
    ),
    {
      type: "paragraph",
      key: "plans"
    },

    {
      type: "header",
      name: "Proportionality of Contribution"
    },
    ...contributionGrid(),
    {
      type: "paragraph",
      text: "Other, if applicable (specify):",
      key: "contrib_other_paragraph",
      optional: true
    }
  ].map((item: CCStructureItem) =>
    item.type === "paragraph"
      ? { ...item, additionalCss: paragraphStyling }
      : item
  ),
  ...deptEMForm(false)
].map((item: CCStructureItem) => setIsMentorField(item, ALL_FIELDS_MENTOR));
